import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Music2, CreditCard, Gift, Check, Loader2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import useAuthStore from '../store/useAuthStore';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Subscribe = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, refreshUser, redeemCode } = useAuthStore();
  
  const [isLoading, setIsLoading] = useState(false);
  const [accessCode, setAccessCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  const sessionId = searchParams.get('session_id');

  // Check payment status if returning from Stripe
  useEffect(() => {
    if (sessionId && token) {
      checkPaymentStatus(sessionId);
    }
  }, [sessionId, token]);

  // Redirect if user already has access
  useEffect(() => {
    if (user?.has_access) {
      navigate('/practice');
    }
  }, [user, navigate]);

  const checkPaymentStatus = async (sid) => {
    setCheckingPayment(true);
    try {
      let attempts = 0;
      const maxAttempts = 5;
      
      while (attempts < maxAttempts) {
        const response = await fetch(`${BACKEND_URL}/api/subscription/status/${sid}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.payment_status === 'paid') {
            setPaymentSuccess(true);
            await refreshUser();
            setTimeout(() => navigate('/practice'), 2000);
            return;
          }
        }
        
        await new Promise(resolve => setTimeout(resolve, 2000));
        attempts++;
      }
    } catch (error) {
      console.error('Payment check error:', error);
    }
    setCheckingPayment(false);
  };

  const handleSubscribe = async () => {
    if (!token) {
      navigate('/auth', { state: { from: { pathname: '/subscribe' } } });
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/subscription/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ origin_url: window.location.origin })
      });

      if (!response.ok) throw new Error('Failed to create checkout');

      const data = await response.json();
      window.location.href = data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      setIsLoading(false);
    }
  };

  const handleRedeemCode = async (e) => {
    e.preventDefault();
    if (!token) {
      navigate('/auth', { state: { from: { pathname: '/subscribe' } } });
      return;
    }

    setCodeError('');
    setIsLoading(true);
    try {
      await redeemCode(accessCode);
      navigate('/practice');
    } catch (error) {
      setCodeError(error.message);
    }
    setIsLoading(false);
  };

  if (checkingPayment) {
    return (
      <div className="min-h-screen noise-bg flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <Loader2 className="w-12 h-12 animate-spin text-accent mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Processing Payment...</h2>
          <p className="text-muted-foreground">Please wait while we confirm your subscription.</p>
        </motion.div>
      </div>
    );
  }

  if (paymentSuccess) {
    return (
      <div className="min-h-screen noise-bg flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-20 h-20 rounded-full bg-emerald-100 dark:bg-emerald-900 flex items-center justify-center mx-auto mb-4">
            <Check className="w-10 h-10 text-emerald-500" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Payment Successful!</h2>
          <p className="text-muted-foreground">Redirecting you to practice...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen noise-bg flex items-center justify-center p-6" data-testid="subscribe-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg"
      >
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center">
            <Music2 className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold">Number System Trainer</span>
        </div>

        {/* Subscription Card */}
        <div className="bg-card border-2 border-border rounded-3xl p-8 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] dark:shadow-[8px_8px_0px_0px_rgba(255,255,255,0.2)]">
          <h1 className="text-2xl font-bold text-center mb-2">Unlock Full Access</h1>
          <p className="text-muted-foreground text-center mb-8">
            Master the Number System with unlimited practice
          </p>

          {/* Price */}
          <div className="text-center mb-8">
            <div className="flex items-baseline justify-center gap-1">
              <span className="text-5xl font-bold">$5</span>
              <span className="text-muted-foreground">/month</span>
            </div>
          </div>

          {/* Features */}
          <ul className="space-y-3 mb-8">
            {[
              'Unlimited flashcard practice',
              'All 12 major keys',
              'AI-powered hints',
              'Timer challenges',
              'Progress tracking'
            ].map((feature, i) => (
              <li key={i} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-accent/10 flex items-center justify-center">
                  <Check className="w-3 h-3 text-accent" />
                </div>
                <span>{feature}</span>
              </li>
            ))}
          </ul>

          {/* Subscribe Button */}
          <Button
            onClick={handleSubscribe}
            disabled={isLoading}
            className="w-full bg-accent hover:bg-accent/90 text-white h-12 text-lg"
            data-testid="subscribe-btn"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <CreditCard className="w-5 h-5 mr-2" />
                Subscribe Now
              </>
            )}
          </Button>

          {/* Divider */}
          <div className="flex items-center gap-4 my-8">
            <div className="flex-1 h-px bg-border" />
            <span className="text-sm text-muted-foreground">or</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {/* Access Code */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Gift className="w-4 h-4 text-accent" />
              <span className="font-medium">Have an access code?</span>
            </div>
            <form onSubmit={handleRedeemCode} className="flex gap-2">
              <Input
                placeholder="Enter code"
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                className="flex-1 uppercase"
                data-testid="access-code-input"
              />
              <Button type="submit" variant="outline" disabled={isLoading || !accessCode} data-testid="redeem-btn">
                Redeem
              </Button>
            </form>
            {codeError && (
              <p className="text-sm text-destructive mt-2" data-testid="code-error">{codeError}</p>
            )}
          </div>
        </div>

        {/* Back to home */}
        <div className="mt-6 text-center">
          <Button variant="ghost" onClick={() => navigate('/')} data-testid="back-home">
            ← Back to Home
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

export default Subscribe;
